import type { UpdateLocationInput } from 'app/generated/gql/graphql'
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { useNavigate, useParams } from 'react-router-dom'
import { State } from 'country-state-city'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { defaultOpenHours } from 'app/lib/officeHours'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { LocationType, OpenHours } from 'app/types/entities'
import { WorkspaceJson } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useUser, useWorkspace } from 'app/utils'
import { logWarn } from 'lib/logging'
import { useLocationStore } from 'app/stores/StoreContext'

// Fields accepted by the GraphQL UpdateLocationInput. openHours is handled
// separately below since it is tracked as React state, not a form field.
const EDITABLE_FIELDS = [
  `name`,
  `type`,
  `address`,
  `city`,
  `state`,
  `zip`,
  `timezone`,
  `businessPhone`,
  `officeMainNumber`,
] as const

const OPEN_HOURS_DAYS = [`sunday`, `monday`, `tuesday`, `wednesday`, `thursday`, `friday`, `saturday`] as const

// Strip GraphQL __typename keys and keep only the shape LocationOpenHoursInput accepts.
function normalizeOpenHours(
  hours: OpenHours,
): Record<string, { hours: { from: string; to: string }[]; open: boolean }> {
  const result: Record<string, { hours: { from: string; to: string }[]; open: boolean }> = {}
  for (const day of OPEN_HOURS_DAYS) {
    const value = hours?.[day]
    if (!value) continue
    result[day] = {
      open: !!value.open,
      hours: (value.hours ?? []).map((range) => ({ from: range.from, to: range.to })),
    }
  }
  return result
}

export type UseLocationSettingsGeneralReturn = {
  user: ReturnType<typeof useUser>
  locationId: string | undefined
  isNew: boolean
  states: { value: string; label: string }[]
  location: LocationType | undefined
  defaultValues: LocationType | undefined
  workspace: WorkspaceJson | undefined
  isDirtyForm: boolean
  isSaving: boolean
  openHours: OpenHours
  formRef: React.RefObject<HTMLFormElement | null>
  canChangeLocationWorkspace: boolean
  setOpenHours: (hours: OpenHours) => void
  setIsDirtyForm: (v: boolean) => void
  onDelete: () => void
  onChangeWorkspace: (newWorkspace: WorkspaceJson) => void
  onSubmit: (data?: Record<string, unknown>) => Promise<void>
}

export function useLocationSettingsGeneral(): UseLocationSettingsGeneralReturn {
  const user = useUser()
  const navigate = useNavigate()
  const { locationId } = useParams()
  const [confirm] = useConfirmation()
  const checkPermission = useCheckPermission()

  const canChangeLocationWorkspace = checkPermission([`org:create:location`, `settings:location`])

  const w = useWorkspace()
  const isNew = locationId === `new`
  const states = State.getStatesOfCountry(`US`).map((s) => ({ value: s.isoCode, label: s.name }))
  const locationStore = useLocationStore()
  const [location, setLocation] = useState<LocationType | undefined>()

  useEffect(() => {
    if (!locationId || locationId === `new`) return
    locationStore
      .fetchOne(locationId)
      .then((result) => {
        const data = result.data as any
        const loc = (data?.location ?? data?.hits?.[0] ?? data) as LocationType | undefined
        if (loc) setLocation(loc)
      })
      .catch(() => {})
  }, [locationId])

  const defaultValues = useMemo(
    () => (location && locationId !== `new` ? { ...location } : undefined),
    [location, locationId],
  )
  const [workspace, setWorkspace] = useState<WorkspaceJson | undefined>(w)
  const [isDirtyForm, setIsDirtyForm] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [openHours, setOpenHours] = useState<OpenHours>(location?.openHours ?? defaultOpenHours)
  const formRef = useRef<HTMLFormElement>(null)

  // Populate hours once the async location fetch resolves. Keyed on `location`
  // (not `locationId`) because the fetch lands after the initial render, so a
  // `locationId`-only dep would leave openHours stuck on defaultOpenHours.
  useEffect(() => {
    setOpenHours(location?.openHours ?? defaultOpenHours)
  }, [location])

  // Reset transient form state when switching to a different location.
  useEffect(() => {
    setWorkspace(w)
    setIsDirtyForm(false)
  }, [locationId])

  useEffect(() => {
    // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
    formRef.current?.addEventListener(`input`, handleInput)
    return () => {
      formRef.current?.removeEventListener(`input`, handleInput)
    }
  }, [formRef.current])

  const handleInput = () => {
    setIsDirtyForm(true)
  }

  const onDelete = () => {
    confirm({
      title: `Delete Location`,
      description: `Are you sure you want to delete this location? It may take several hours for all location data to be deleted.`,
      variant: `danger`,
    })
      .then(async () => {
        await locationStore.remove(location!.id!)
        notifySuccess(`Location deleted`)
        setIsDirtyForm(false)
        navigate(`/settings/location-settings?wId=${w?.id}`)
      })
      .catch((err) => {
        logWarn(`Error deleting the location`, err)
        notifyError(`There was an error deleting the location`)
      })
  }

  const onChangeWorkspace = (newWorkspace: WorkspaceJson) => {
    confirm({
      title: `Change Workspace`,
      description: `Are you sure you want to change this location's Workspace? It may take several hours for data to be moved.`,
      variant: `danger`,
    })
      .then(() => setWorkspace(newWorkspace))
      .catch(() => {})
  }

  const onSubmit = async (data?: Record<string, unknown>) => {
    if (!data) return

    const input: UpdateLocationInput = {}
    for (const field of EDITABLE_FIELDS) {
      const value = data[field]
      if (typeof value === `string`) input[field] = value
    }

    // openHours is tracked as React state (not a native form field), and the
    // fetched value may carry GraphQL __typename keys that the input type rejects.
    input.openHours = normalizeOpenHours(openHours)

    setIsSaving(true)
    try {
      if (isNew) {
        if (!input.name || !input.timezone) {
          notifyError(`Name and timezone are required`)
          return
        }
        const result = await locationStore.create({
          name: input.name,
          timezone: input.timezone,
          type: input.type,
          address: input.address,
          city: input.city,
          state: input.state,
          zip: input.zip,
          businessPhone: input.businessPhone,
          officeMainNumber: input.officeMainNumber,
          openHours: input.openHours,
        })
        if (result.error) {
          logWarn(`Error creating the location`, result.error)
          notifyError(`There was an error creating the location`)
          return
        }
        const created = (result.data as { createLocation?: LocationType } | undefined)?.createLocation
        if (created) {
          setIsDirtyForm(false)
          notifySuccess(`Location created`)
          navigate(`/settings/location-settings/${created.id}/general?wId=${workspace?.id}`)
        } else {
          logWarn(`Unexpected response from createLocation`, result.data)
          notifyError(`There was an error creating the location`)
        }
      } else {
        if (!locationId) return
        const result = await locationStore.update(locationId, input)
        if (result.error) {
          logWarn(`Error saving the location`, result.error)
          notifyError(`There was an error saving the location`)
          return
        }
        const updated = (result.data as { updateLocation?: LocationType } | undefined)?.updateLocation
        if (updated) setLocation(updated)
        setIsDirtyForm(false)
        notifySuccess(`Location saved`)
      }
    } catch (err) {
      logWarn(`Error saving the location`, err)
      notifyError(`There was an error saving the location`)
    } finally {
      setIsSaving(false)
    }
  }

  return {
    user,
    locationId,
    isNew,
    states,
    location,
    defaultValues,
    workspace,
    isDirtyForm,
    isSaving,
    openHours,
    formRef,
    canChangeLocationWorkspace,
    setOpenHours,
    setIsDirtyForm,
    onDelete,
    onChangeWorkspace,
    onSubmit,
  }
}
